import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Timeline } from 'react-event-timeline';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import InfiniteScroll from 'react-infinite-scroller';
import YesNoModal from '@/shared/components/YesNoModal';
import moment from 'moment';
import DocumentService from '@/shared/services/DocumentService';
import ToastrService from '@/shared/services/ToastrService';
import api from '@/shared/api';
import config from '@/config';
import intlShape from '@/shared/utils/intlShape';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import SignalRService from '@/shared/services/SignalRService';
import Axios from 'axios';
import { injectStateService, StateService } from '@/shared/services/StateService';
import handleHttpError from '@/shared/services/HandleHttpError';
import SingleRelationMailModal from '@/shared/components/email/SingleRelationMailModal';
import ShareItemModal from '@/shared/components/ShareItemModal';
import ForwardEmailModal from '@/shared/components/email/ForwardEmailModal';
import DataCacheService from '@/shared/services/DataCacheService';
import ReplyToEmailModal from '@/shared/components/email/ReplyToEmailModal';
import utils from '@/shared/utils/adminpulse';
import FileSaver from 'file-saver';
import Button from '@/shared/components/Button';
import { FaFilePdf } from 'react-icons/fa';
import AuthService, { privileges, functionalities, features } from '@/shared/services/AuthService';
import copy from 'copy-to-clipboard';
import RelationSummary from './RelationSummary';
import InteractionFilters from './InteractionFilters';
import DocumentDetailsModal from '../documents/details/DocumentDetailsModal';
import CreateToDoModal from '../tasks/CreateToDoModal';
import ToDoSuggestionsModal from './ToDoSuggestionsModal';
import messages from './interactionMessages';
import InteractionEvent from './InteractionEvent';
import CreateInteractionModal from './CreateInteractionModal';
import TaskDetailsModal from '../tasks/taskDetails/TaskDetailsModal';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import DocumentSelectorModal from '@/shared/components/DocumentSelectorModal';
import AttachmentSelectionModal from './AttachmentSelectionModal';
import SummaryEditModal from './SummaryEditModal';

const defaultFilters = {
  showLinkedRelationInteractions: true,
  search: null,
  startDate: null,
  period: 'all',
  tags: [],
  interactionTypes: [],
};

const todoRequestParameters = {
  desc: false,
  page: 0,
  mainStatus: [0, 1, 3],
  ownCompanies: [],
  period: 'all',
  relationTypes: [],
  taskLevels: [2], // Todo's
  sortKey: 'deadline',
  take: 50,
};

const dontShowAgainStorageKey = 'InteractionsAIWarningDontShowAgain';
const collapsedStorageKey = 'InteractionsCollapsed';

class InteractionsContainer extends Component {
  constructor(props) {
    super(props);
    const { searchParams } = props;

    let storageFilters;
    if (
      !searchParams?.interactionFilters.interactionId &&
      localStorage.getItem('InteractionsOverviewFilters')
    ) {
      storageFilters = JSON.parse(localStorage.getItem('InteractionsOverviewFilters')) || {};
      if (storageFilters.startDate) storageFilters.startDate = moment(storageFilters.startDate);
      if (storageFilters.endDate) storageFilters.endDate = moment(storageFilters.endDate);
      delete storageFilters.interactionId; // prevent the interactionId that was saved in storage from displaying only this interaction
      // we cant fix this in the "set storage", because some customers already have this property in their storage unfortunately
    } else {
      storageFilters = {};
    }

    this.state = {
      interactions: null,
      relation: null,
      todos: null,
      completedTodos: null,
      collapsed: sessionStorage.getItem(collapsedStorageKey) === 'true',
      showDeleteInteractionModal: false,
      showEmailDocumentModal: false,
      showCreateTodoModal: false,
      showShareItemModal: false,
      showTaskDetailsModal: false,
      showDocumentSelectorModal: false,
      activeTaskId: null,
      showToDoSuggestionsModal: false,
      todoSuggestions: [],
      activeInteractionForSuggestions: null,
      generatingAI: false,
      loadingTodos: false,
      loadingSummary: false,
      showGenerateConfirmModal: false,
      showSummaryConfirmModal: false,
      showTodoAttachmentSelectionModal: false,
      showSummaryAttachmentSelectionModal: false,
      showSummaryEditModal: false,
      generatedSummary: null,
      selectedInteraction: null,
      selectedAttachmentIds: [],
      activeDocument: null,
      activeInteraction: null,
      hasMoreData: true,
      scrollKey: 0,
      searchParams: {
        page: -1,
        take: 10,
        ...searchParams,
        interactionFilters: {
          ...defaultFilters,
          ...storageFilters,
          ...searchParams.interactionFilters,
        },
      },
    };
  }

  componentDidMount() {
    this.loadRelationSummary();
    SignalRService.subscribe('interactionCreated', this.interactionCreatedHandler);
  }

  componentWillUnmount() {
    SignalRService.unsubscribe('interactionCreated', this.interactionCreatedHandler);
    if (this.summaryToken && !this.summaryToken.token.reason) {
      this.summaryToken.cancel('Operation canceled');
    }
  }

  toggleCollapsed = () => {
    this.setState(({ collapsed }) => {
      const newCollapsed = !collapsed;
      sessionStorage.setItem(collapsedStorageKey, String(newCollapsed));
      return { collapsed: newCollapsed };
    });
  };

  interactionCreatedHandler = interaction => {
    const { relation } = this.state;
    if (!relation || relation.id !== interaction.relationId) return;

    this.setState(state => ({
      ...state,
      showDummyInteraction: false,
      interactions: [
        ...((state.interactions || []).filter(x => x.isPinned) || []),
        interaction,
        ...((state.interactions || []).filter(x => !x.isPinned) || []),
      ],
    }));
  };

  loadRelationSummary = () => {
    const { searchParams } = this.state;
    const { relationId } = searchParams.interactionFilters;
    const { openMailModal, intl } = this.props;

    const relTodoRequestParameters = {
      relationId,
      desc: false,
      page: 0,
      mainStatus: [0, 1, 3],
      ownCompanies: [],
      period: 'all',
      relationTypes: [],
      taskLevels: [2], // Todo's
      sortKey: 'deadline',
      take: 50,
    };

    this.summaryToken = Axios.CancelToken.source();
    const taskPromise = api.base.post(
      `${config.appUrls.tasks}/api/TaskList/`,
      relTodoRequestParameters,
      {
        cancelToken: this.summaryToken.token,
      },
    );
    const relationPromise = api.base.get(
      `${config.appUrls.api}/api/relation/summary?id=${relationId}`,
      {
        cancelToken: this.summaryToken.token,
      },
    );
    return Promise.all([relationPromise, taskPromise])
      .then(([{ data: relationData }, { data: taskData }]) => {
        const todos = taskData ? taskData.tasks : [];
        this.setState({ relation: relationData, showSendEmailModal: openMailModal, todos }, () =>
          this.loadInteractions(),
        );

        if (relationData.eidDocumentPhotoId) {
          DocumentService.getLink(relationData.eidDocumentPhotoId).then(result => {
            this.setState(state => ({
              ...state,
              relation: {
                ...state.relation,
                eidPhotoUrl: result,
              },
            }));
          });
        }
      })
      .catch(handleHttpError(intl));
  };

  loadInteractions(incomingSearchParams = null) {
    const { searchParams, loading } = this.state;
    const { intl } = this.props;
    if (loading) return;
    this.setState({ loading: true });

    const params = incomingSearchParams || searchParams;
    const newSearchParams = {
      ...params,
      page: params.page + 1,
    };

    return api.base
      .post(`${config.appUrls.interactions}/api/interactions`, {
        ...params,
        ...params.interactionFilters,
        page: newSearchParams.page,
      })
      .then(({ data }) => {
        const storageFilters = { ...newSearchParams.interactionFilters };
        delete storageFilters.relationId; // dont store relationId in local storage
        localStorage.setItem('InteractionsOverviewFilters', JSON.stringify(storageFilters));

        return this.refreshState(data, newSearchParams);
      })
      .catch(handleHttpError(intl))
      .finally(() => this.setState({ loading: false }));
  }

  loadMoreTodos = searchParams => {
    const { intl } = this.props;

    this.summaryToken = Axios.CancelToken.source();

    const { relation } = this.state;
    const { desc, sortKey, page, pageSize, ...otherParams } = searchParams;
    const params = {
      desc,
      sort: sortKey,
      page,
      take: pageSize,
      ...otherParams,
      relationId: relation.id,
    };

    return api.base
      .post(`${config.appUrls.tasks}/api/TaskList/`, params, {
        cancelToken: this.summaryToken.token,
      })
      .then(({ data }) => {
        const { tasks = [] } = data || {};
        this.setState(({ todos }) => {
          if (params.page === 0) {
            return {
              todos: tasks,
            };
          }

          if (todos.length > params.page * params.take) {
            // When a todo is created it is appended to the list.
            // But if you scroll down, it could also be in the next page request
            // So if there is a duplicate todo, we keep the one from the request because it is in the right order (sorting)
            const newItems = todos.filter(x => tasks.findIndex(t => t.id === x.id) < 0);
            return {
              items: [...newItems, ...todos],
            };
          }
          return {
            todos: [...items, ...todos],
          };
        });
        return tasks;
      })
      .catch(handleHttpError(intl));
  };

  exportToPdf() {
    const { searchParams } = this.state;
    const { intl } = this.props;

    const model = {
      ...searchParams.interactionFilters,
      languageCode: 'nl',
      signalRConnectionId: SignalRService.getConnectionId(),
    };

    this.setState({ creatingPdf: true });
    return api.base
      .post(`${config.appUrls.interactions}/api/interactions/pdf`, model)
      .then(() => {
        ToastrService.info(intl.formatMessage(messages.ExportToPdfStarted));
      })
      .catch(handleHttpError(intl))
      .finally(() => this.setState({ creatingPdf: false }));
  }

  onDocumentUploaded = (error, document, interaction) => {
    const { intl } = this.props;

    if (error) {
      ToastrService.error(intl.formatMessage(messages.MsgUploadAttachmentFailed));
      return;
    }
    const { interactions } = this.state;

    this.setState({
      interactions: interactions.map(x => {
        if (x.id === interaction.id) {
          return {
            ...x,
            attachments: [...(x.attachments || []), document],
          };
        }
        return x;
      }),
    });
  };

  retrieveCompletedTodosForInteractions = interactionIds => {
    if (
      !interactionIds ||
      !interactionIds.length ||
      !AuthService.hasPermission({
        functionality: functionalities.Todo,
        privilege: privileges.read,
      })
    )
      return;
    const { relation } = this.state;
    const relTodoRequestParameters = {
      relationId: relation.id,
      desc: false,
      page: 0,
      mainStatus: [2], // Completed
      ownCompanies: [],
      period: 'all',
      relationTypes: [],
      taskLevels: [2], // Todo's
      SelectedTasks: interactionIds,
      sortKey: 'deadline',
      take: 50,
    };

    this.summaryToken = Axios.CancelToken.source();
    api.base
      .post(`${config.appUrls.tasks}/api/TaskList/`, relTodoRequestParameters, {
        cancelToken: this.summaryToken.token,
      })
      .then(({ data }) => {
        const completedTodos = data ? data.tasks : [];
        this.setState({ completedTodos: [...completedTodos] });
      });
  };

  retrieveLinkedTodosForEmailInteractions = emailDocumentIds => {
    if (
      !emailDocumentIds ||
      !emailDocumentIds.length ||
      !AuthService.hasPermission({
        functionality: functionalities.Documents,
        privilege: privileges.read,
      })
    )
      return;

    const { intl } = this.props;

    return api.base
      .post(
        `${config.appUrls.documents}/api/documents/DocumentLinksByDocumentIds/5`,
        emailDocumentIds,
      )
      .then(({ data }) => {
        if (!data || !data.length);
        const { interactions } = this.state;

        for (let index = 0; index < data.length; index++) {
          const docLink = data[index];
          const interaction = interactions.find(x => x.objectId === docLink.documentId);
          if (interaction) {
            interaction.links = [
              ...(interaction.links || []),
              { interactionId: interaction.id, linkId: docLink.linkId, linkType: 1 },
            ];
          }
        }

        this.setState({ interactions });
      })
      .catch(handleHttpError(intl));
  };

  retrieveDocumentsForInteractions = interactionIds => {
    if (
      !interactionIds ||
      !interactionIds.length ||
      !AuthService.hasPermission({
        functionality: functionalities.Documents,
        privilege: privileges.read,
      })
    )
      return;

    const { intl } = this.props;

    return api.base
      .post(`${config.appUrls.documents}/api/documents/DocumentLinks`, interactionIds)
      .then(({ data }) => {
        if (!data || !data.length);
        const { interactions } = this.state;

        for (let index = 0; index < data.length; index++) {
          const document = data[index];

          const linkIds = document.links.filter(y => y.linkType === 6).map(y => y.linkId);

          if (!linkIds || !linkIds.length) {
            // eslint-disable-next-line no-continue
            continue;
          }

          linkIds.forEach(interactionId => {
            const interaction = interactions.find(x => x.id === interactionId);
            if (interaction) {
              interaction.attachments = [...(interaction.attachments || []), document];
            }
          });
        }

        this.setState({ interactions });
      })
      .catch(handleHttpError(intl));
  };

  deleteAttachment = (interactionId, attachment) => {
    const { intl } = this.props;

    api.base
      .delete(`${config.appUrls.documents}/api/documents/links/${attachment.id}/${interactionId}/6`)
      .then(() => {
        const { interactions } = this.state;

        ToastrService.success(intl.formatMessage(messages.AttachmentDeleted));

        const interaction = interactions.find(x => x.id === interactionId);
        if (!interaction) return;

        interaction.attachments = interaction.attachments.filter(x => x.id !== attachment.id);

        this.setState({
          interactions,
        });
      });
  };

  refreshState(data, newSearchParams) {
    const { scrollKey, searchParams } = this.state;
    const increaseInfiniteScrollKey = newSearchParams.page === 0 && searchParams.page !== 0;

    // if the user did not customize the filters, and there are no interactions present, we're going to display a dummy
    // interaction.
    const noCustomFilters =
      (!newSearchParams.interactionFilters.period ||
        newSearchParams.interactionFilters.period === 'all') &&
      (!newSearchParams.interactionFilters.interactionTypes ||
        !newSearchParams.interactionFilters.interactionTypes.length);

    return this.setState(
      state => ({
        ...state,
        hasLinkedRelations: data.hasLinkedRelations,
        interactions:
          newSearchParams.page > 0
            ? [...state.interactions, ...data.interactions]
            : [...data.interactions],
        loading: false,
        forceLoad: false,
        hasMoreData: data.interactions.length === newSearchParams.take,
        scrollKey: increaseInfiniteScrollKey ? state.scrollKey + 1 : scrollKey,
        showDummyInteraction:
          !data.interactions.length && noCustomFilters && newSearchParams.page === 0,
        searchParams: {
          ...newSearchParams,
        },
      }),
      () => this.onInteractionsLoaded(data.interactions),
    );
  }

  onInteractionsLoaded = interactions => {
    const interactionIds = interactions.map(x => x.id);
    this.retrieveDocumentsForInteractions(interactionIds);
    const emailDocumentIds = interactions
      .filter(x => x.objectType === 2 && x.objectId)
      .map(x => x.objectId);
    this.retrieveLinkedTodosForEmailInteractions(emailDocumentIds);
    this.retrieveCompletedTodosForInteractions(interactionIds);
  };

  onEditInteraction = interaction => {
    const utcDate = moment.utc(interaction.date);
    const localDate = utcDate.local();
    const minutes = moment.duration(localDate.format('HH:mm')).asMinutes();

    // old interaction weren't created using the rich text editor. This causes newlines (\n) to be present in the description string.
    // these are not converted to new lines in HTML, so we replace them with a <br/> tag
    let { description } = interaction;
    if (description && !description.includes('html>')) {
      description = description.replace(/\n/g, '<br />');
    }

    this.setState(state => ({
      ...state,
      interactions: (state.interactions || []).map(x => {
        if (x.id === interaction.id) {
          return {
            ...x,
            description,
            originalDescription: description,
            originalSummary: x.summary,
            editFocus: true,
            originalTitle: x.title,
            originalDate: x.date,
            originalTags: x.tags,
            minutes,
          };
        }
        // cancel all other edits
        if (!x.editFocus) return x;
        return {
          ...x,
          title: x.originalTitle,
          description: x.originalDescription,
          summary: x.originalSummary,
          date: x.originalDate,
          tags: x.originalTags,
          editFocus: false,
        };
      }),
    }));
  };

  cancelEdit = interactionId =>
    this.setState(({ interactions }) => ({
      interactions: interactions.map(x => {
        if (x.id === interactionId) {
          return {
            ...x,
            editFocus: false,
            title: x.originalTitle,
            description: x.originalDescription,
            summary: x.originalSummary,
            date: x.originalDate,
            tags: x.originalTags,
          };
        }
        return x;
      }),
    }));

  onSaveInteraction = interaction => {
    const { intl } = this.props;

    const model = {
      ...interaction,
    };

    if (!model.date) {
      ToastrService.error(intl.formatMessage(messages.DateMandatory));
      return;
    }

    if (!model.title && !model.description) {
      ToastrService.error(intl.formatMessage(messages.TitleOrDescriptionMandatory));
      return;
    }
    const time = moment.duration(model.minutes, 'minutes');
    const interactionDate = moment.utc(model.date);

    model.date = moment([
      interactionDate.year(),
      interactionDate.month(),
      interactionDate.date(),
      time.hours(),
      time.minutes(),
      0,
      0,
    ]);

    return this.sendUpdateRequest(model).then(({ data }) => {
      this.setState(state => ({
        ...state,
        interactions: (state.interactions || []).map(x => {
          if (x.id === interaction.id) {
            return {
              ...data,
              attachments: [...(interaction.attachments || [])],
            };
          }
          return x;
        }),
      }));
    });
  };

  sendUpdateRequest = interaction =>
    api.base.put(`${config.appUrls.interactions}/api/interactions`, interaction);

  linkDocumentsToInteraction = viewModel => {
    const { intl } = this.props;
    return api.base
      .post(`${config.appUrls.documents}/api/documents/AddDocumentsToLink`, viewModel)
      .catch(handleHttpError(intl));
  };

  onCreateInteraction = interaction => {
    const { searchParams } = this.state;
    const { intl } = this.props;
    const { relationId } = searchParams.interactionFilters;
    const model = {
      ...interaction,
      creationSource: 0,
      relationId,
    };

    return api.base
      .post(`${config.appUrls.interactions}/api/interactions/create`, model)
      .then(({ data }) => {
        if (!data) return;
        if (interaction.attachments && interaction.attachments.length) {
          const interactionId = data.id;

          const addDocumentsToInteractionViewModel = {
            linkId: interactionId,
            links: interaction.attachments.map(x => ({
              documentId: x.id,
              linkType: 6,
            })),
          };

          data.attachments = interaction.attachments;

          this.linkDocumentsToInteraction(addDocumentsToInteractionViewModel);
        }

        this.setState(state => ({
          ...state,
          interactions: [
            ...(state.interactions || []).filter(x => x.isPinned),
            data,
            ...(state.interactions || []).filter(x => !x.isPinned),
          ],
          showDummyInteraction: false,
          showCreateInteractionModal: false,
        }));
      })
      .catch(handleHttpError(intl));
  };

  showDeleteInteractionConfirmation = interaction => {
    this.setState(state => ({
      ...state,
      showDeleteInteractionModal: true,
      activeInteraction: interaction,
    }));
  };

  onDeleteInteraction = () => {
    const { activeInteraction } = this.state;

    return api.base
      .delete(`${config.appUrls.interactions}/api/interactions/${activeInteraction.id}`)
      .then(() => {
        this.setState(state => ({
          ...state,
          showDeleteInteractionModal: false,
          activeInteraction: undefined,
          interactions: (state.interactions || []).filter(x => x.id !== activeInteraction.id),
        }));
      });
  };

  filtersChanged = filters => {
    const { searchParams } = this.state;
    const searchCriteria = {
      ...searchParams,
      page: -1,
      interactionFilters: {
        ...filters,
      },
    };
    this.setState({ searchParams: searchCriteria, forceLoad: true });
    this.loadInteractions(searchCriteria);
  };

  openCreateInteractionDialog = interactionType => {
    this.setState(state => ({
      ...state,
      activeInteractionType: interactionType,
      showCreateInteractionModal: true,
    }));
  };

  getDummyInteraction = () => {
    const { intl } = this.props;
    const interaction = {
      interactionType: 1,
      date: moment(),
      title: intl.formatMessage(messages.DummyInteractionTitle),
      description: intl.formatMessage(messages.DummyInteractionDescription),
    };
    return interaction;
  };

  onPinInteraction = interaction => {
    if (interaction.isPinned) return;

    const model = {
      ...interaction,
      isPinned: true,
    };

    const { intl } = this.props;

    const { interactions } = this.state;

    return this.sendUpdateRequest(model).then(({ data }) => {
      ToastrService.success(intl.formatMessage(messages.InteractionPinned));

      const pinnedInteractions = [
        ...interactions.filter(x => x.isPinned),
        {
          ...data,
          attachments: model.attachments,
        },
      ].sort(dynamicSort('-date'));

      this.setState(
        state => ({
          ...state,
          interactions: [
            ...pinnedInteractions,
            ...(state.interactions || []).filter(x => !x.isPinned && x.id !== data.id),
          ],
        }),
        () => window.scrollTo(0, 0),
      );
    });
  };

  onRemovePin = interaction => {
    if (!interaction.isPinned) return;

    const model = {
      ...interaction,
      isPinned: false,
    };
    const { intl } = this.props;
    const { interactions } = this.state;
    return this.sendUpdateRequest(model).then(({ data }) => {
      ToastrService.success(intl.formatMessage(messages.InteractionUnpinned));

      const unpinnedInteractions = [
        ...interactions.filter(x => !x.isPinned),
        {
          ...data,
          attachments: model.attachments,
        },
      ].sort(dynamicSort('-date'));

      this.setState(state => ({
        ...state,
        interactions: [
          ...(state.interactions || []).filter(x => x.isPinned && x.id !== data.id),
          ...unpinnedInteractions,
        ],
      }));
    });
  };

  copyLinkToInteraction = interaction => {
    const { intl } = this.props;
    const url = `${config.appUrls.app}/#/interactions/${interaction.relationId}/${interaction.id}`;
    if (copy(url)) {
      ToastrService.success(intl.formatMessage(messages.ValueCopiedToClipboard));
    }
  };

  exportSingleInteractionToPdf = interaction => {
    if (interaction.exportingToPdf) return;
    const { intl } = this.props;
    const languageCode = intl.locale;
    const { interactions } = this.state;

    this.setState({
      interactions: interactions.map(x =>
        x.id === interaction.id ? { ...x, exportingToPdf: true } : x,
      ),
    });
    const endpoint = `${config.appUrls.interactions}/api/interactions/${interaction.id}/pdf/${
      languageCode.split('-')[0]
    }`;

    return api.base
      .get(endpoint, {
        responseType: 'arraybuffer',
      })
      .then(result => {
        const fileType = result.headers['content-type'];
        const fileName = interaction.title ? `${interaction.title}.pdf` : 'untitled.pdf';
        const blob = new Blob([result.data], {
          type: fileType,
        });
        FileSaver.saveAs(blob, fileName);
        this.setState({
          interactions: interactions.map(x =>
            x.id === interaction.id ? { ...x, exportingToPdf: false } : x,
          ),
        });
      })
      .catch(error => {
        this.setState({
          interactions: interactions.map(x =>
            x.id === interaction.id ? { ...x, exportingToPdf: false } : x,
          ),
        });
        const errorObject = utils.arrayBufferToObject(error.response && error.response.data);
        handleHttpError(intl)(errorObject);
      });
  };

  sendInteractionViaEmail = async interaction => {
    if (interaction.sendingViaEmail) return;
    const { intl } = this.props;
    const languageCode = intl.locale;
    const { interactions } = this.state;

    this.setState({
      interactions: interactions.map(x =>
        x.id === interaction.id ? { ...x, sendingViaEmail: true } : x,
      ),
    });
    const endpoint = `${config.appUrls.interactions}/api/interactions/${interaction.id}/pdf/${
      languageCode.split('-')[0]
    }/save`;
    const result = await api.base.get(endpoint);

    this.setState({
      interactions: interactions.map(x =>
        x.id === interaction.id ? { ...x, sendingViaEmail: false } : x,
      ),
    });

    const { data } = result;
    if (!data) {
      ToastrService.error(
        intl.formatMessage(messages.SomethingWentWrongWhileConvertingTheInteractionToPdf),
      );
    }

    this.setState({
      showSendEmailModal: true,
      activeAttachments: [{ id: data.documentId, name: data.filename }],
    });
  };

  openCreateTodoModal = interaction => {
    const { intl } = this.props;
    if (interaction && interaction.objectType === 2 && interaction.objectId) {
      if (interaction.document) {
        // Cached document info
        this.setState({ showCreateTodoModal: true, activeDocument: interaction.document });
      } else {
        return api.base
          .get(`${config.appUrls.documents}/api/Documents/Info/${interaction.objectId}`)
          .then(({ data }) => {
            this.setState(state => ({
              interactions: (state.interactions || []).map(x =>
                x.id === interaction.id ? { ...x, document: data } : x,
              ),
              activeDocument: data,
              showCreateTodoModal: true,
            }));
            return data;
          })
          .catch(handleHttpError(intl));
      }
    } else if (interaction && interaction.interactionType != 2) {
      this.setState({
        showCreateTodoModal: true,
        activeDocument: null,
        activeInteraction: interaction,
      });
    } else {
      this.setState({ showCreateTodoModal: true, activeDocument: null, activeInteraction: null });
    }
  };

  handleInteractionChanged = (id, change = {}) => {
    this.setState(({ interactions }) => ({
      interactions: interactions.map(x => {
        if (x.id === id) {
          return {
            ...x,
            ...change,
          };
        }
        return x;
      }),
    }));
  };

  showTaskDetailsModal = taskId => {
    this.setState({ showTaskDetailsModal: true, activeTaskId: taskId });
  };

  handleTasksUpdated = updatedTasks => {
    if (!updatedTasks) return;
    this.setState(({ todos }) => ({
      todos: todos.map(row => {
        const updated = updatedTasks.find(x => x.id === row.id);
        if (!updated) return row;
        return { ...row, ...updated };
      }),
    }));
  };

  onCloseTaskDetail = tasks => {
    if (tasks && tasks.length) {
      this.handleTasksUpdated(tasks);
    }
    this.setState({ showTaskDetailsModal: false, activeTaskId: null });
  };

  onSaveLinkedInteraction = linkedInteraction => {
    const { interactions, activeInteraction, activeDocument } = this.state;
    const interactionId = activeInteraction
      ? activeInteraction.id
      : interactions.find(x => x.objectId === activeDocument.id)?.id;
    const interaction = interactions.find(x => x.id === interactionId);
    if (!interaction) return;

    const linkedInteractions = interaction.links || [];
    linkedInteraction.id = interactionId;
    this.setState({
      interactions: interactions.map(x => {
        if (x.id === interactionId) {
          return {
            ...x,
            links: [...linkedInteractions, linkedInteraction],
          };
        }
        return x;
      }),
    });
  };

  handleToDoSuggestionsModalClose = () => {
    this.setState({
      showToDoSuggestionsModal: false,
      todoSuggestions: [],
      activeInteractionForSuggestions: null,
      loadingTodos: false,
    });
  };

  handleToDoSuggestionsAccepted = createdTodos => {
    const { activeInteractionForSuggestions } = this.state;

    if (createdTodos.length > 0) {
      // Update interaction links to include the new todos
      const newLinks = createdTodos.map(todo => ({
        linkId: todo.id,
        linkType: 1, // linkType for todos
      }));

      this.setState(({ todos, interactions }) => ({
        showToDoSuggestionsModal: false,
        todoSuggestions: [],
        activeInteractionForSuggestions: null,
        todos: [...createdTodos, ...todos],
        // Update the interaction to include links to the new todos
        interactions: interactions.map(interaction => {
          if (interaction.id === activeInteractionForSuggestions?.id) {
            return {
              ...interaction,
              links: [...(interaction.links || []), ...newLinks],
            };
          }
          return interaction;
        }),
      }));
    } else {
      this.setState({
        showToDoSuggestionsModal: false,
        todoSuggestions: [],
        activeInteractionForSuggestions: null,
      });
    }
  };

  generateTodos = interaction => {
    // Check if interaction has attachments
    if (interaction.attachments && interaction.attachments.length > 0) {
      // Show attachment selection modal first
      this.setState({
        showTodoAttachmentSelectionModal: true,
        selectedInteraction: interaction,
      });
      return;
    }

    // No attachments, proceed with normal flow
    const skipConfirmation = localStorage.getItem(dontShowAgainStorageKey) === 'true';

    if (skipConfirmation) {
      // Skip modal and generate directly
      this.setState(
        {
          selectedInteraction: interaction,
        },
        () => {
          this.confirmGenerateTodos(false, []); // false = don't save again since already saved, empty attachments
        },
      );
    } else {
      // Show confirmation modal first
      this.setState({
        showGenerateConfirmModal: true,
        selectedInteraction: interaction,
      });
    }
  };

  confirmGenerateTodos = (dontShowAgain = false, attachmentIds = []) => {
    const { intl } = this.props;
    const { selectedInteraction } = this.state;

    // Save "don't show again" preference if checkbox was checked
    if (dontShowAgain) {
      localStorage.setItem(dontShowAgainStorageKey, 'true');
    }

    if (!selectedInteraction) return Promise.resolve();

    // Immediately show result modal with loading state and close all other modals
    this.setState({
      showGenerateConfirmModal: false,
      showTodoAttachmentSelectionModal: false,
      showToDoSuggestionsModal: true,
      loadingTodos: true,
      todoSuggestions: [],
      activeInteractionForSuggestions: selectedInteraction,
    });

    ToastrService.info(intl.formatMessage(messages.GeneratingToDoSuggestions));

    const requestBody = {};
    if (attachmentIds && attachmentIds.length > 0) {
      requestBody.attachmentIds = attachmentIds;
    }

    return api.base
      .post(
        `${config.appUrls.interactions}/api/interactions/${selectedInteraction.id}/generatetodos`,
        requestBody,
      )
      .then(({ data }) => {
        this.setState({
          loadingTodos: false,
          todoSuggestions: data,
          selectedInteraction: null,
        });
      })
      .catch(error => {
        this.setState({
          loadingTodos: false,
          showToDoSuggestionsModal: false,
          selectedInteraction: null,
        });
        if (error.response?.data?.code) {
          handleHttpError(intl)(error);
        } else {
          ToastrService.warning(
            intl.formatMessage(messages.SomethingWentWrongWhileGeneratingToDos),
          );
        }
      });
  };

  cancelGenerateTodos = () => {
    this.setState({
      showGenerateConfirmModal: false,
      selectedInteraction: null,
    });
  };

  summarizeInteraction = interaction => {
    // Check if interaction has attachments
    if (interaction.attachments && interaction.attachments.length > 0) {
      // Show attachment selection modal first
      this.setState({
        showSummaryAttachmentSelectionModal: true,
        selectedInteraction: interaction,
      });
      return;
    }

    // No attachments, proceed with normal flow
    const skipConfirmation = localStorage.getItem(dontShowAgainStorageKey) === 'true';

    if (skipConfirmation) {
      // Skip modal and summarize directly
      this.setState(
        {
          selectedInteraction: interaction,
        },
        () => {
          this.confirmSummarizeInteraction(false, []); // false = don't save again since already saved, empty attachments
        },
      );
    } else {
      // Show confirmation modal first
      this.setState({
        showSummaryConfirmModal: true,
        selectedInteraction: interaction,
      });
    }
  };

  confirmSummarizeInteraction = (dontShowAgain = false, attachmentIds = []) => {
    const { intl } = this.props;
    const { selectedInteraction } = this.state;

    // Save "don't show again" preference if checkbox was checked
    if (dontShowAgain) {
      localStorage.setItem(dontShowAgainStorageKey, 'true');
    }

    if (!selectedInteraction) return Promise.resolve();

    // Immediately show result modal with loading state and close all other modals
    this.setState({
      showSummaryConfirmModal: false,
      showSummaryAttachmentSelectionModal: false,
      showSummaryEditModal: true,
      loadingSummary: true,
      generatedSummary: '',
    });

    ToastrService.info(intl.formatMessage(messages.GeneratingSummary));

    const requestBody = {};
    if (attachmentIds && attachmentIds.length > 0) {
      requestBody.attachmentIds = attachmentIds;
    }

    return api.base
      .post(
        `${config.appUrls.interactions}/api/interactions/${selectedInteraction.id}/summarize`,
        requestBody,
      )
      .then(({ data }) => {
        this.setState({
          loadingSummary: false,
          generatedSummary: data,
        });
        ToastrService.success(intl.formatMessage(messages.SummaryGenerated));
      })
      .catch(error => {
        this.setState({
          loadingSummary: false,
          showSummaryEditModal: false,
          selectedInteraction: null,
        });
        if (error.response?.data?.code) {
          handleHttpError(intl)(error);
        } else {
          ToastrService.warning(
            intl.formatMessage(messages.SomethingWentWrongWhileGeneratingSummary),
          );
        }
      });
  };

  cancelSummarizeInteraction = () => {
    this.setState({
      showSummaryConfirmModal: false,
      selectedInteraction: null,
    });
  };

  handleSummarySave = editedSummary => {
    const { intl } = this.props;
    const { selectedInteraction } = this.state;

    if (!selectedInteraction) {
      this.setState({
        showSummaryEditModal: false,
        generatedSummary: null,
      });
      return;
    }

    const updatedInteraction = {
      ...selectedInteraction,
      summary: editedSummary,
    };

    return this.sendUpdateRequest(updatedInteraction)
      .then(({ data }) => {
        this.setState(state => ({
          showSummaryEditModal: false,
          generatedSummary: null,
          selectedInteraction: null,
          interactions: (state.interactions || []).map(interaction =>
            interaction.id === selectedInteraction.id
              ? { ...data, attachments: interaction.attachments }
              : interaction,
          ),
        }));
        ToastrService.success(intl.formatMessage(messages.SummarySaved));
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  handleSummaryEditCancel = () => {
    this.setState({
      showSummaryEditModal: false,
      generatedSummary: null,
      selectedInteraction: null,
      loadingSummary: false,
    });
  };

  handleTodoAttachmentSelection = selectedAttachmentIds => {
    this.confirmGenerateTodos(false, selectedAttachmentIds);
  };

  handleSummaryAttachmentSelection = selectedAttachmentIds => {
    this.confirmSummarizeInteraction(false, selectedAttachmentIds);
  };

  cancelTodoAttachmentSelection = () => {
    this.setState({
      showTodoAttachmentSelectionModal: false,
      selectedInteraction: null,
    });
  };

  cancelSummaryAttachmentSelection = () => {
    this.setState({
      showSummaryAttachmentSelectionModal: false,
      selectedInteraction: null,
    });
  };

  openDocumentSelectorModal = interaction => {
    this.setState({
      showDocumentSelectorModal: true,
      activeInteraction: interaction,
    });
  };

  handleDocumentsSelected = selectedDocuments => {
    const { activeInteraction, interactions } = this.state;
    if (!activeInteraction || !selectedDocuments || !selectedDocuments.length) {
      this.setState({ showDocumentSelectorModal: false, activeInteraction: null });
      return;
    }

    const existingIds = new Set((activeInteraction.attachments || []).map(doc => doc.id));
    const newDocuments = selectedDocuments.filter(doc => !existingIds.has(doc.id));

    if (!newDocuments.length) {
      this.setState({ showDocumentSelectorModal: false, activeInteraction: null });
      return;
    }

    const links = newDocuments.map(doc => ({
      documentId: doc.id,
      linkType: 6,
    }));

    this.linkDocumentsToInteraction({
      linkId: activeInteraction.id,
      links,
    }).then(() => {
      this.setState(state => {
        const updatedAttachments = [...(activeInteraction.attachments || []), ...newDocuments];
        return {
          showDocumentSelectorModal: false,
          activeInteraction: null,
          interactions: (state.interactions || []).map(x =>
            x.id === activeInteraction.id ? { ...x, attachments: updatedAttachments } : x,
          ),
        };
      });
    });
  };

  render() {
    const {
      interactions,
      searchParams,
      hasMoreData,
      scrollKey,
      loading,
      showDeleteInteractionModal,
      showCreateInteractionModal,
      relation,
      forceLoad,
      activeInteractionType,
      showDummyInteraction,
      activeInteraction,
      activeDocument,
      showEmailDocumentModal,
      hasLinkedRelations,
      showSendEmailModal,
      showCreateTodoModal,
      showShareItemModal,
      showForwardModal,
      showReplyToEmailModal,
      replyAll,
      creatingPdf,
      activeAttachments,
      todos,
      showTaskDetailsModal,
      activeTaskId,
      showDocumentSelectorModal,
      showToDoSuggestionsModal,
      todoSuggestions,
      activeInteractionForSuggestions,
      generatingAI,
      loadingTodos,
      loadingSummary,
      showGenerateConfirmModal,
      showSummaryConfirmModal,
      showTodoAttachmentSelectionModal,
      showSummaryAttachmentSelectionModal,
      showSummaryEditModal,
      generatedSummary,
      selectedInteraction,
      collapsed,
    } = this.state;
    const { intl, stateService } = this.props;
    const noDataFound =
      !hasMoreData && (!interactions || interactions.length <= 0) && !showDummyInteraction;
    const showTimeLine =
      (!forceLoad && !!interactions && !!interactions.length) ||
      (!forceLoad && showDummyInteraction);
    const showFilters = noDataFound || !!interactions;

    let documentUploadEndpoint = `${config.appUrls.documents}/api/documents?documentType=13`;
    if (relation && relation.id) {
      documentUploadEndpoint += `&relationId=${relation.id}`;
    }

    const canViewDocuments = AuthService.hasPermission({
      functionality: functionalities.Documents,
      privilege: privileges.read,
    });

    return (
      <div className="interaction-screen">
        {showSendEmailModal && (
          <SingleRelationMailModal
            intl={intl}
            show
            relationId={relation.id}
            companyId={relation.companyId}
            emailContext={13}
            attachments={activeAttachments}
            onClose={() => this.setState({ showSendEmailModal: false })}
            onSave={() => this.setState({ showSendEmailModal: false })}
          />
        )}
        <div className="col-md-4 col-lg-3 flex-col margin20B">
          {relation && (
            <RelationSummary
              relation={relation}
              onOpenCreateInteractionDialog={interactionType =>
                this.openCreateInteractionDialog(interactionType)
              }
              onSendEmail={() => this.setState({ showSendEmailModal: true })}
              onOpenCreateTodoDialog={() => this.openCreateTodoModal()}
              todos={todos}
              showTaskDetailsModal={this.showTaskDetailsModal}
              handleTasksUpdated={this.handleTasksUpdated}
              loadMoreTodos={this.loadMoreTodos}
              initialTodoRequestParams={todoRequestParameters}
              showTodoList={true}
            />
          )}
        </div>
        <div className="col-lg-9 col-md-8 flex-col">
          {showFilters && (
            <div className="panel">
              <div className="panel-heading">
                <h3 className="panel-title">
                  {intl.formatMessage(messages.DummyInteractionTitle)}{' '}
                  <ZendeskHelp article="5434756079122" />
                </h3>
              </div>
              <div className="panel-body">
                <InteractionFilters
                  onFilterChange={this.filtersChanged}
                  filters={searchParams.interactionFilters}
                  hasLinkedRelations={hasLinkedRelations}
                />
              </div>
            </div>
          )}
          {interactions && (
            <div className="row btn-row">
              <div className="col-md-6 col-md-offset-6 text-right">
                <Button
                  btnClassName="btn btn-xs btn-default margin5R"
                  onClick={this.toggleCollapsed}
                  title={intl.formatMessage(collapsed ? messages.ExpandAll : messages.CollapseAll)}
                >
                  <i className={`fa ${collapsed ? 'fa-expand' : 'fa-compress'} margin5R`} />
                  {intl.formatMessage(collapsed ? messages.ExpandAll : messages.CollapseAll)}
                </Button>
                {canViewDocuments && (
                  <Button
                    btnClassName="btn btn-xs btn-default"
                    onClick={() => this.exportToPdf()}
                    disabled={!interactions || creatingPdf}
                  >
                    <span className="margin5R">
                      {!creatingPdf && <FaFilePdf className="file-icon" />}
                      {creatingPdf && <i className="fa fa-spin fa-spinner" />}
                    </span>
                    {intl.formatMessage(messages.ExportSearchResultsToPdf)}
                  </Button>
                )}
              </div>
            </div>
          )}
          <div className="timeline-wrapper">
            {!interactions && loading && <LoadingAnimation />}
            {forceLoad && <LoadingAnimation />}
            {!loading && noDataFound && (
              <span style={{ marginLeft: '60px' }}>
                <FormattedMessage {...messages.NoInteractionsFound} />
              </span>
            )}
            {showTimeLine && (
              <Timeline
                className="interactions-timeline"
                style={{ fontSize: '1.6rem', width: 'auto', margin: '0 0 0 1.6rem' }}
                lineColor="#d6d6d6"
                lineStyle={{
                  width: '4px',
                  top: '-62px',
                  height: 'calc(100% + 62px',
                }}
              >
                <InfiniteScroll
                  pageStart={searchParams.page}
                  loadMore={page => this.loadInteractions()}
                  hasMore={hasMoreData}
                  initialLoad={false}
                  key={scrollKey}
                  loader={loading ? <span key={1} /> : <LoadingAnimation key={0} />}
                >
                  {showDummyInteraction && (
                    <InteractionEvent interaction={this.getDummyInteraction()} allowEdit={false} />
                  )}
                  {interactions.map(interaction => (
                    <div
                      id="interactions-container"
                      key={interaction.id}
                      onMouseEnter={() =>
                        this.setState(state => ({
                          ...state,
                          interactions: (state.interactions || []).map(x => {
                            if (x.id === interaction.id) {
                              return {
                                ...x,
                                focused: true,
                              };
                            }
                            return x;
                          }),
                        }))
                      }
                      onMouseLeave={() =>
                        this.setState(state => ({
                          ...state,
                          interactions: (state.interactions || []).map(x => {
                            if (x.id === interaction.id) {
                              return {
                                ...x,
                                focused: false,
                              };
                            }
                            return x;
                          }),
                        }))
                      }
                    >
                      <InteractionEvent
                        interaction={interaction}
                        collapsed={collapsed}
                        stateService={stateService}
                        documentUploadEndpoint={documentUploadEndpoint}
                        showRelationName={
                          searchParams.interactionFilters &&
                          searchParams.interactionFilters.showLinkedRelationInteractions
                        }
                        modifiedBy={DataCacheService.getUserName(interaction.modifiedBy)}
                        onEditInteraction={() => this.onEditInteraction(interaction)}
                        onSaveInteraction={() => this.onSaveInteraction(interaction)}
                        onShareItem={() =>
                          this.setState({
                            activeInteraction: interaction,
                            showShareItemModal: true,
                          })
                        }
                        onExportToPdf={() => this.exportSingleInteractionToPdf(interaction)}
                        onCopyLinkToInteraction={() => this.copyLinkToInteraction(interaction)}
                        onSendViaEmail={() => this.sendInteractionViaEmail(interaction)}
                        onDeleteInteraction={() =>
                          this.showDeleteInteractionConfirmation(interaction)
                        }
                        onPinInteraction={() => this.onPinInteraction(interaction)}
                        onRemovePin={() => this.onRemovePin(interaction)}
                        onOpenEmail={() =>
                          this.setState(state => ({
                            ...state,
                            showEmailDocumentModal: true,
                            activeInteraction: interaction,
                          }))
                        }
                        onForwardEmail={() =>
                          this.setState(state => ({
                            ...state,
                            showForwardModal: true,
                            activeInteraction: interaction,
                          }))
                        }
                        onReplyToEmail={replyAllParameter =>
                          this.setState({
                            showReplyToEmailModal: true,
                            activeInteraction: interaction,
                            replyAll: replyAllParameter,
                          })
                        }
                        linkedTodos={[
                          ...(todos
                            ? todos.filter(todo =>
                                interaction.links.some(link => link.linkId === todo.id),
                              )
                            : []),
                          ...(this.state.completedTodos
                            ? this.state.completedTodos.filter(todo =>
                                interaction.links.some(link => link.linkId === todo.id),
                              )
                            : []),
                        ]}
                        onCreateTodo={() => this.openCreateTodoModal(interaction)}
                        onGenerateTodos={() => this.generateTodos(interaction)}
                        generatingAI={loadingTodos || loadingSummary}
                        onSummarizeInteraction={() => this.summarizeInteraction(interaction)}
                        onInteractionChanged={this.handleInteractionChanged}
                        onEditCancel={() => this.cancelEdit(interaction.id)}
                        onDeleteAttachment={this.deleteAttachment}
                        onDocumentUploaded={this.onDocumentUploaded}
                        showTaskDetailsModal={this.showTaskDetailsModal}
                        handleTasksUpdated={this.handleTasksUpdated}
                        allowEdit={true}
                        onSelectDocumentsFromAdminPulse={() =>
                          this.openDocumentSelectorModal(interaction)
                        }
                      />
                    </div>
                  ))}
                  {loading && <LoadingAnimation />}
                </InfiniteScroll>
              </Timeline>
            )}
          </div>
        </div>
        {showShareItemModal && (
          <ShareItemModal
            show
            type="interaction"
            id={activeInteraction.id}
            relationId={relation.id}
            relationName={relation.name}
            itemName={`${intl.formatMessage(
              listItemMsg('InteractionType', activeInteraction.interactionType),
            )} - ${activeInteraction.title}`}
            onClose={() => this.setState({ showShareItemModal: false })}
          />
        )}
        {showDeleteInteractionModal && (
          <YesNoModal
            intl={intl}
            show
            title={intl.formatMessage(messages.Delete)}
            message={intl.formatMessage(messages.DeleteInteractionConfirmationMsg)}
            onNo={() =>
              this.setState(state => ({
                ...state,
                showDeleteInteractionModal: false,
                activeInteraction: undefined,
              }))
            }
            onSave={this.onDeleteInteraction}
          />
        )}
        {showCreateInteractionModal && (
          <CreateInteractionModal
            show
            interactionType={activeInteractionType}
            onSave={this.onCreateInteraction}
            invoiceStreamId={relation.invoiceStreamId}
            relationId={relation.id}
            documentUploadEndpoint={documentUploadEndpoint}
            onDeleteAttachment={this.deleteAttachment}
            onClose={() => this.setState({ showCreateInteractionModal: false })}
            stateService={stateService}
          />
        )}
        {showEmailDocumentModal && (
          <DocumentDetailsModal
            show
            documentId={activeInteraction.objectId}
            allowNavigation={false}
            onSave={() =>
              this.setState({ showEmailDocumentModal: false, activeInteraction: undefined })
            }
            onClose={() =>
              this.setState({ showEmailDocumentModal: false, activeInteraction: undefined })
            }
          />
        )}
        {showForwardModal && (
          <ForwardEmailModal
            intl={intl}
            documentId={activeInteraction.objectId}
            relationId={relation.id}
            show
            onClose={() =>
              this.setState(state => ({
                ...state,
                showForwardModal: false,
              }))
            }
          />
        )}
        {showReplyToEmailModal && (
          <ReplyToEmailModal
            intl={intl}
            documentId={activeInteraction.objectId}
            relationId={relation.id}
            replyAll={replyAll}
            show
            onClose={() =>
              this.setState({
                showReplyToEmailModal: false,
              })
            }
          />
        )}
        {showCreateTodoModal && (
          <CreateToDoModal
            show
            documents={activeDocument ? [activeDocument] : []}
            interaction={activeInteraction}
            relationId={relation.id}
            onSave={newTodo =>
              this.setState({
                showCreateTodoModal: false,
                activeDocument: null,
                activeInteraction: null,
                todos: [newTodo, ...todos],
              })
            }
            onClose={() =>
              this.setState({
                showCreateTodoModal: false,
                activeDocument: null,
                activeInteraction: null,
              })
            }
            onSaveLinkedInteraction={linkedInteraction =>
              this.onSaveLinkedInteraction(linkedInteraction)
            }
          />
        )}
        {showTaskDetailsModal && (
          <TaskDetailsModal
            show
            task={{ id: activeTaskId }}
            onSave={this.onCloseTaskDetail}
            onClose={this.onCloseTaskDetail}
          />
        )}
        {showGenerateConfirmModal && (
          <YesNoModal
            show={showGenerateConfirmModal}
            title={intl.formatMessage(messages.ConfirmGenerateToDos)}
            message={intl.formatMessage(messages.ConfirmGenerateToDoMessage)}
            onSave={dontShowAgain =>
              this.confirmGenerateTodos(dontShowAgain, this.state.selectedAttachmentIds)
            }
            onNo={this.cancelGenerateTodos}
            onClose={this.cancelGenerateTodos}
            showDontShowAgain={true}
          />
        )}
        {showSummaryConfirmModal && (
          <YesNoModal
            show={showSummaryConfirmModal}
            title={intl.formatMessage(messages.ConfirmGenerateSummary)}
            message={intl.formatMessage(messages.ConfirmGenerateSummaryMessage)}
            onSave={dontShowAgain =>
              this.confirmSummarizeInteraction(dontShowAgain, this.state.selectedAttachmentIds)
            }
            onNo={this.cancelSummarizeInteraction}
            onClose={this.cancelSummarizeInteraction}
            showDontShowAgain={true}
          />
        )}
        {showToDoSuggestionsModal && (
          <ToDoSuggestionsModal
            show={showToDoSuggestionsModal}
            suggestions={todoSuggestions}
            interaction={activeInteractionForSuggestions}
            relationId={relation.id}
            onSave={this.handleToDoSuggestionsAccepted}
            onClose={this.handleToDoSuggestionsModalClose}
            loading={loadingTodos}
          />
        )}
        {showDocumentSelectorModal && (
          <DocumentSelectorModal
            show
            initialRelation={
              this.state.activeInteraction?.relationId || this.state.activeInteraction?.relation?.id
            }
            onDocumentsSelected={this.handleDocumentsSelected}
            onClose={() =>
              this.setState({
                showDocumentSelectorModal: false,
                activeInteraction: null,
              })
            }
          />
        )}
        {showTodoAttachmentSelectionModal && selectedInteraction && (
          <AttachmentSelectionModal
            show={showTodoAttachmentSelectionModal}
            attachments={selectedInteraction.attachments || []}
            onConfirm={this.handleTodoAttachmentSelection}
            onClose={this.cancelTodoAttachmentSelection}
            title={intl.formatMessage(messages.ConfirmGenerateToDos)}
            description={intl.formatMessage(messages.ConfirmGenerateToDoMessageWithAttachments)}
          />
        )}
        {showSummaryAttachmentSelectionModal && selectedInteraction && (
          <AttachmentSelectionModal
            show={showSummaryAttachmentSelectionModal}
            attachments={selectedInteraction.attachments || []}
            onConfirm={this.handleSummaryAttachmentSelection}
            onClose={this.cancelSummaryAttachmentSelection}
            title={intl.formatMessage(messages.ConfirmGenerateSummary)}
            description={intl.formatMessage(messages.ConfirmGenerateSummaryMessageWithAttachments)}
          />
        )}
        {showSummaryEditModal && (
          <SummaryEditModal
            show={showSummaryEditModal}
            initialSummary={generatedSummary}
            onSave={this.handleSummarySave}
            onClose={this.handleSummaryEditCancel}
            loading={loadingSummary}
          />
        )}
      </div>
    );
  }
}

InteractionsContainer.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
};

export default injectStateService(injectIntl(InteractionsContainer));
